import React, { useEffect, useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Star } from 'lucide-react';
import { useUpsell } from './UpsellContext';
// ---- Shopify Cart Permalink config ----
const SHOPIFY_CART_DOMAIN = 'http://impulsodigital.vip/cart/';
const MAIN_PRODUCT_VARIANT_ID = '44827187904598';
const FULL_PACK_VARIANT_ID = '44827258290262';
const FULL_PACK_PRICE = 9990;
const MAIN_PRODUCT_PRICE = 19990;
const FULL_PACK_IMAGE = 'https://cdn.magicpatterns.com/uploads/pZzUkCDbqUwmu41bcniKfT/pack_3_ebooks_premium_comprimido.webp';
interface Upsell {
  variantId: string;
  name: string;
  benefit: string;
  price: number;
  image: string;
}
const upsells: Upsell[] = [{
  variantId: '44827142717526',
  name: 'La Biblia del Tejido a Dos Agujas',
  benefit: '+2.000 patrones para dominar el tejido a dos agujas.',
  price: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/u3wj5XqgocJ8sg7wyPumcm/biblia_tejido_dos_agujas_comprimido.webp'
}, {
  variantId: '44827153104982',
  name: 'Plantitas Tejidas que se Venden Solas',
  benefit: 'Plantitas decorativas ideales para regalar o vender.',
  price: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/5Zz8fBW8C7FkDVq4Jwuex8/plantitas_tejidas_comprimido.webp'
}, {
  variantId: '44827178041430',
  name: 'Mandalas a Crochet — La Colección Completa',
  benefit: 'Diseños únicos de mandalas para decorar y regalar.',
  price: 3990,
  image: 'https://cdn.magicpatterns.com/uploads/wj3ZVdPhcQ8yc2RMsjEAm2/mandalas_crochet_comprimido.webp'
}];
const formatPrice = (value: number) => '$' + value.toLocaleString('es-AR');
function buildCheckoutUrl(selected: Upsell[]) {
  const items = [`${MAIN_PRODUCT_VARIANT_ID}:1`];
  selected.forEach(p => items.push(`${p.variantId}:1`));
  return `${SHOPIFY_CART_DOMAIN}${items.join(',')}`;
}
export function UpsellModal() {
  const {
    isOpen,
    closeUpsell
  } = useUpsell();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeUpsell();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, closeUpsell]);
  const toggle = (variantId: string) => {
    setSelectedIds(prev => prev.includes(variantId) ? prev.filter(id => id !== variantId) : [...prev, variantId]);
  };
  const selectedUpsells = upsells.filter(u => selectedIds.includes(u.variantId));
  const upsellsTotal = selectedUpsells.reduce((sum, u) => sum + u.price, 0);
  const grandTotal = MAIN_PRODUCT_PRICE + upsellsTotal;
  const hasSelection = selectedUpsells.length > 0;
  const goToCheckout = () => {
    window.location.href = buildCheckoutUrl(selectedUpsells);
  };
  const goToFullPackCheckout = () => {
    window.location.href = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1,${FULL_PACK_VARIANT_ID}:1`;
  };
  const goToMainOnlyCheckout = () => {
    window.location.href = `${SHOPIFY_CART_DOMAIN}${MAIN_PRODUCT_VARIANT_ID}:1`;
  };
  return <AnimatePresence data-id="element-226">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4" aria-modal="true" role="dialog" aria-labelledby="upsell-title" data-id="element-227">
          {/* Overlay */}
          <div className="absolute inset-0 bg-dark/50 backdrop-blur-sm" onClick={closeUpsell} data-id="element-228" />

          {/* Modal */}
          <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 40,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="relative z-10 w-full sm:max-w-md bg-cream rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[90vh] overflow-hidden" data-id="element-229">
            {/* Header */}
            <div className="flex-shrink-0 bg-white px-4 py-3 border-b border-rose-light/20 relative text-center" data-id="element-230">
              <button onClick={closeUpsell} aria-label="Cerrar" className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full hover:bg-cream flex items-center justify-center text-dark/50 hover:text-dark transition-colors" data-id="element-231">
                <X className="w-5 h-5" data-id="element-232" />
              </button>
              <h2 id="upsell-title" className="text-base sm:text-lg font-bold text-dark font-serif pr-8" data-id="element-233">
                Completá tu biblioteca creativa 🧶
              </h2>
              <p className="text-xs text-dark/55 mt-0.5" data-id="element-234">
                Sumá ebooks antes de finalizar tu compra.
              </p>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-id="element-235">
              {/* BEST OFFER — full pack */}
              <div className="rounded-xl border-2 border-rose-deep bg-white overflow-hidden shadow-soft" data-id="element-236">
                <div className="bg-rose-deep text-white text-center py-1.5 px-3 flex items-center justify-center gap-1.5 font-bold text-xs tracking-wide uppercase" data-id="element-237">
                  <Star className="w-3.5 h-3.5 fill-current" data-id="element-238" /> Mejor oferta ·
                  La más elegida
                </div>
                <div className="p-4" data-id="element-239">
                  <div className="flex gap-4" data-id="element-240">
                    <img src={FULL_PACK_IMAGE} alt="Pack exclusivo de 3 ebooks premium" className="flex-shrink-0 w-24 h-auto self-start rounded-lg drop-shadow-sm" loading="lazy" decoding="async" data-id="element-241" />
                    <div className="flex-1 min-w-0" data-id="element-242">
                      <h3 className="text-[15px] font-bold text-dark leading-snug mb-1.5" data-id="element-243">
                        Los 3 ebooks extra por {formatPrice(FULL_PACK_PRICE)}
                      </h3>
                      <ul className="space-y-1 mb-2" data-id="element-244">
                        {upsells.map(u => <li key={u.variantId} className="flex items-start gap-1.5 text-xs text-dark/70 leading-snug" data-id="element-245">
                            <Check className="w-3.5 h-3.5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-246" />
                            <span data-id="element-247">{u.name}</span>
                          </li>)}
                      </ul>
                      <div className="flex items-baseline gap-2" data-id="element-248">
                        <span className="text-2xl font-bold text-rose-deep" data-id="element-249">
                          {formatPrice(FULL_PACK_PRICE)}
                        </span>
                        <span className="text-sm text-dark/40 line-through" data-id="element-250">
                          {formatPrice(11970)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button onClick={goToFullPackCheckout} className="w-full mt-4 bg-rose-deep text-white font-bold py-3.5 px-4 rounded-full hover:bg-rose-deep/90 transition-colors text-[15px]" data-id="element-251">
                    Sí, quiero los 3 ebooks
                  </button>
                </div>
              </div>

              {/* Divider */}
              <div className="flex items-center gap-3 text-dark/40 text-[11px] font-medium uppercase tracking-widest" data-id="element-252">
                <div className="flex-1 h-px bg-rose-light/40" data-id="element-253" />
                <span data-id="element-254">o sumá de a uno</span>
                <div className="flex-1 h-px bg-rose-light/40" data-id="element-255" />
              </div>

              {/* Individual upsells */}
              <div className="space-y-2" data-id="element-256">
                {upsells.map(u => {
              const checked = selectedIds.includes(u.variantId);
              return <button key={u.variantId} type="button" onClick={() => toggle(u.variantId)} aria-pressed={checked} className={`w-full text-left flex items-center gap-3 bg-white rounded-xl p-3 border-2 transition-all ${checked ? 'border-rose-deep shadow-soft' : 'border-transparent hover:border-rose-light/50'}`} data-id="element-257">
                      <img src={u.image} alt={`Portada de ${u.name}`} className="flex-shrink-0 w-14 h-auto rounded-md drop-shadow-sm" loading="lazy" decoding="async" data-id="element-258" />
                      <div className="flex-1 min-w-0" data-id="element-259">
                        <p className="font-semibold text-dark text-[15px] leading-snug mb-0.5" data-id="element-260">
                          {u.name}
                        </p>
                        <p className="text-xs text-dark/55 leading-snug mb-1 line-clamp-2" data-id="element-261">
                          {u.benefit}
                        </p>
                        <p className="text-base font-bold text-rose-deep" data-id="element-262">
                          {formatPrice(u.price)}
                        </p>
                      </div>
                      <span className={`flex-shrink-0 w-7 h-7 rounded-md border-2 flex items-center justify-center transition-colors ${checked ? 'bg-rose-deep border-rose-deep' : 'border-rose-light bg-white'}`} data-id="element-263">
                        {checked && <Check className="w-4 h-4 text-white" data-id="element-264" />}
                      </span>
                    </button>;
            })}
              </div>
            </div>

            {/* Fixed bottom bar */}
            <div className="flex-shrink-0 bg-white border-t border-rose-light/20 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" data-id="element-265">
              {hasSelection && <div className="flex justify-between items-baseline text-sm mb-2" data-id="element-266">
                  <span className="text-dark/60" data-id="element-267">Total de tu compra</span>
                  <span className="text-lg font-bold text-rose-deep" data-id="element-268">
                    {formatPrice(grandTotal)}
                  </span>
                </div>}

              <button onClick={goToCheckout} className="w-full bg-rose-deep text-white font-bold py-3.5 px-4 rounded-full hover:bg-rose-deep/90 transition-colors text-sm" data-id="element-269">
                {hasSelection ? `Agregar y continuar · ${formatPrice(grandTotal)}` : 'Continuar solo con mi compra'}
              </button>

              <button onClick={goToMainOnlyCheckout} className="w-full mt-2 text-dark/60 hover:text-dark text-sm font-semibold py-3 rounded-full border border-rose-light/40 hover:bg-cream/60 transition-colors" data-id="element-270">
                No gracias, continuar
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}